import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, clean } from "../../utils/sqlClient";
import { syncHubSpotData } from "../../utils/hubspotSync";
import { fmtDate } from "../../utils/format";
import BidPackageAISummary from "./BidPackageAISummary";

const HUBSPOT_PORTAL_ID = "44218274";

const DOC_TYPES = ["Plans", "Specs", "Addendum", "Email", "RFI", "Other"];

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHead({ icon, title, action }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 6 }}>
        <span>{icon}</span>{title}
      </div>
      {action}
    </div>
  );
}

export default function BidPackage({ projectId, project, bidPkg, onBidPkgChange }) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [editing, setEditing]   = useState(!bidPkg);
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [docs, setDocs]           = useState([]);
  const [specSections, setSpecSections] = useState([]);
  const [loadingDocs, setLoadingDocs]   = useState(false);
  const [showAddDoc, setShowAddDoc]     = useState(false);
  const [showAddSpec, setShowAddSpec]   = useState(false);

  // Form state — mirrors BidPackages columns. Before a bid package exists,
  // fields fall back to the Estimates row, which the HubSpot deal webhook keeps
  // current (bid_due_date <- proposal_due_date, delivery method, SharePoint).
  const [form, setForm] = useState({
    delivery_method:      bidPkg?.DeliveryMethod      || project?.DeliveryMethod || "Design-Build",
    sharepoint_url:       bidPkg?.SharePointUrl        || project?.SharePointFolderUrl || "",
    general_contractor:   bidPkg?.GeneralContractor    || "",
    architect:            bidPkg?.Architect             || "",
    engineer:             bidPkg?.Engineer              || "",
    bid_due_date:         bidPkg?.BidDueDate            ? bidPkg.BidDueDate.slice(0,10)
                          : project?.BidDueDate         ? project.BidDueDate.slice(0,10) : "",
    issue_date:           bidPkg?.IssueDate             ? bidPkg.IssueDate.slice(0,10) : "",
    addenda_count:        bidPkg?.AddendaCount          ?? 0,
    special_requirements: bidPkg?.SpecialRequirements  || "",
    bond_required:        bidPkg?.BondRequired          ?? false,
    bond_percent:         bidPkg?.BondPercent           || "",
    insurance_notes:      bidPkg?.InsuranceNotes        || "",
    wage_requirements:    bidPkg?.WageRequirements      || "",
    cleanroom_iso_class:  bidPkg?.CleanroomISOClass     || "",
    project_sf:           bidPkg?.ProjectSF             || "",
    project_location:     bidPkg?.ProjectLocation       || "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── HubSpot prefill ───────────────────────────────────────────────────────────
  // Pulls the deal's associated companies (labeled GC / Architect / Engineer in
  // HubSpot), square footage, and description, filling only fields that are
  // still empty — never overwriting anything already typed or saved.
  const [hsSyncing, setHsSyncing] = useState(false);
  const [hsFilled, setHsFilled]   = useState(null); // null = never ran; [] = ran, nothing to fill
  const hsAutoRan = useRef(false);

  async function syncFromHubSpot(auto = false) {
    if (!project?.HubSpotDealID) {
      if (!auto) alert("This estimate has no linked HubSpot deal.");
      return;
    }
    setHsSyncing(true);
    try {
      const data = await syncHubSpotData(project.HubSpotDealID, null, null);
      const byLabel = (patterns) => (data.companies || []).find(c =>
        patterns.some(p => (c.label || "").toLowerCase().includes(p)));
      const gc   = byLabel(["general contractor", "gc", "construction manager"]);
      const arch = byLabel(["architect"]);
      const eng  = byLabel(["engineer"]);
      const locCo = gc || (data.companies || [])[0];

      const filled = [];
      const next = { ...form };
      const fill = (key, value, label) => {
        if (value && !String(next[key] || "").trim()) { next[key] = String(value); filled.push(label); }
      };
      fill("general_contractor", gc?.name, "GC");
      fill("architect", arch?.name, "Architect");
      fill("engineer", eng?.name, "Engineer");
      fill("project_sf", data.squareFeet, "Project SF");
      fill("project_location", locCo?.city && locCo?.state ? `${locCo.city}, ${locCo.state}` : null, "Location");
      fill("special_requirements", data.dealDescription, "Deal description → special requirements");
      if (filled.length) setForm(next);
      setHsFilled(filled);
    } catch (e) {
      if (!auto) alert("HubSpot sync failed: " + e.message);
      console.warn("HubSpot bid-package sync failed:", e.message);
    } finally {
      setHsSyncing(false);
    }
  }

  // First visit before a bid package exists: pull HubSpot automatically so the
  // estimator lands on a mostly-filled brief instead of a blank form.
  useEffect(() => {
    if (hsAutoRan.current || bidPkg || !project?.HubSpotDealID) return;
    hsAutoRan.current = true;
    syncFromHubSpot(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load docs + spec sections when bid package exists
  useEffect(() => {
    if (!bidPkg?.BidPackageID) return;
    setLoadingDocs(true);
    Promise.all([
      sqlRead(`EXEC sp_GetBidPackageDocuments @bid_package_id=${bidPkg.BidPackageID}`),
      sqlRead(`EXEC sp_GetSpecSections @bid_package_id=${bidPkg.BidPackageID}`),
    ]).then(([d, s]) => { setDocs(d); setSpecSections(s); })
      .catch(console.error)
      .finally(() => setLoadingDocs(false));
  }, [bidPkg?.BidPackageID]);

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      const q = `EXEC sp_SaveBidPackage
        @project_id=${projectId},
        @delivery_method='${clean(form.delivery_method)}',
        @sharepoint_url='${clean(form.sharepoint_url)}',
        @general_contractor='${clean(form.general_contractor)}',
        @architect='${clean(form.architect)}',
        @engineer='${clean(form.engineer)}',
        @bid_due_date=${form.bid_due_date ? `'${form.bid_due_date}'` : "NULL"},
        @issue_date=${form.issue_date ? `'${form.issue_date}'` : "NULL"},
        @addenda_count=${+form.addenda_count || 0},
        @special_requirements='${clean(form.special_requirements)}',
        @bond_required=${form.bond_required ? 1 : 0},
        @bond_percent=${form.bond_percent ? +form.bond_percent : "NULL"},
        @insurance_notes='${clean(form.insurance_notes)}',
        @wage_requirements='${clean(form.wage_requirements)}',
        @cleanroom_iso_class='${clean(form.cleanroom_iso_class)}',
        @project_sf=${form.project_sf ? +form.project_sf : "NULL"},
        @project_location='${clean(form.project_location)}',
        @saved_by='${clean(user?.name || "")}'`;
      const rows = await sqlRead(q);
      if (rows[0]?.BidPackageID) {
        onBidPkgChange({ ...form, BidPackageID: rows[0].BidPackageID, DeliveryMethod: form.delivery_method });
        setEditing(false);
      }
    } catch(e) { setSaveError(e.message); }
    setSaving(false);
  };

  const labelStyle = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };
  const inputStyle = { height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="panel-title">Bid Package</div>
          <div className="panel-sub">Key project parameters, document links, and spec sections. This is the guiding brief for the estimate.</div>
        </div>
        {!editing && bidPkg && (
          <button
            onClick={() => setEditing(true)}
            style={{ padding: "7px 14px", fontSize: 12, fontWeight: 500, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "none", cursor: "pointer", color: "var(--text2)" }}
          >
            Edit
          </button>
        )}
      </div>

      {/* ── HubSpot context strip — what the deal already knows ── */}
      {project?.HubSpotDealID && (
        <div style={{
          display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
          padding: "9px 14px", marginBottom: 12,
          background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "var(--radius)",
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.07em" }}>HubSpot</span>
          {[["Client", project?.ClientName], ["Deal owner", project?.DealOwner], ["Estimator", project?.AssignedEstimator]]
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <span key={k} style={{ fontSize: 12, color: "var(--text2)" }}>
                <span style={{ color: "var(--text3)" }}>{k}:</span> <strong style={{ fontWeight: 600 }}>{v}</strong>
              </span>
            ))}
          <span style={{ flex: 1 }} />
          {hsFilled?.length > 0 && (
            <span style={{ fontSize: 11, color: "#15803d", fontWeight: 600 }}>
              ✓ Pulled from deal: {hsFilled.join(", ")}
            </span>
          )}
          {hsFilled?.length === 0 && !hsSyncing && (
            <span style={{ fontSize: 11, color: "var(--text3)" }}>Synced — nothing new to fill</span>
          )}
          <button
            onClick={() => syncFromHubSpot(false)}
            disabled={hsSyncing}
            title="Fill empty brief fields from the HubSpot deal (companies, square footage, description)"
            style={{ padding: "4px 12px", fontSize: 11, fontWeight: 700, background: "none", border: "1px solid #fed7aa", borderRadius: 100, cursor: "pointer", color: "#c2410c", opacity: hsSyncing ? 0.6 : 1 }}
          >
            {hsSyncing ? "Syncing…" : "↻ Sync from HubSpot"}
          </button>
          <a
            href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${project.HubSpotDealID}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: 11, fontWeight: 600, color: "#c2410c", textDecoration: "none" }}
          >
            Open deal ↗
          </a>
        </div>
      )}

      {/* ── Header fields ── */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "16px 18px", marginBottom: 16 }}>
        <SectionHead icon="📋" title="Project Brief" />

        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Row 1 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Delivery method</label>
                <select value={form.delivery_method} onChange={e => set("delivery_method", e.target.value)} style={{ ...inputStyle, height: 30 }}>
                  <option>Design-Build</option>
                  <option>Plan-and-Spec</option>
                  <option>Both</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>ISO Class</label>
                <input type="text" value={form.cleanroom_iso_class} onChange={e => set("cleanroom_iso_class", e.target.value)} placeholder="e.g. ISO 7 / Class 10,000" style={inputStyle} />
              </div>
            </div>
            {/* Row 2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Bid due date</label>
                <input type="date" value={form.bid_due_date} onChange={e => set("bid_due_date", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Issue date</label>
                <input type="date" value={form.issue_date} onChange={e => set("issue_date", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Addenda count</label>
                <input type="number" value={form.addenda_count} onChange={e => set("addenda_count", e.target.value)} min={0} style={inputStyle} />
              </div>
            </div>
            {/* Row 3 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Project SF</label>
                <input type="number" value={form.project_sf} onChange={e => set("project_sf", e.target.value)} placeholder="e.g. 4800" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <input type="text" value={form.project_location} onChange={e => set("project_location", e.target.value)} placeholder="City, State" style={inputStyle} />
              </div>
            </div>
            {/* Row 4 — GC / Arch / Eng */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>General contractor</label>
                <input type="text" value={form.general_contractor} onChange={e => set("general_contractor", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Architect</label>
                <input type="text" value={form.architect} onChange={e => set("architect", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Engineer</label>
                <input type="text" value={form.engineer} onChange={e => set("engineer", e.target.value)} style={inputStyle} />
              </div>
            </div>
            {/* Bond + Insurance + Wage */}
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr", gap: 12, alignItems: "start" }}>
              <div>
                <label style={labelStyle}>Bond req.</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, height: 30 }}>
                  <input type="checkbox" checked={form.bond_required} onChange={e => set("bond_required", e.target.checked)} style={{ width: 14, height: 14 }} />
                  <span style={{ fontSize: 12 }}>Required</span>
                </div>
              </div>
              {form.bond_required && (
                <div>
                  <label style={labelStyle}>Bond %</label>
                  <input type="number" value={form.bond_percent} onChange={e => set("bond_percent", e.target.value)} placeholder="e.g. 100" style={inputStyle} />
                </div>
              )}
              <div>
                <label style={labelStyle}>Wage requirements</label>
                <input type="text" value={form.wage_requirements} onChange={e => set("wage_requirements", e.target.value)} placeholder="e.g. Davis-Bacon Act" style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Insurance notes</label>
              <input type="text" value={form.insurance_notes} onChange={e => set("insurance_notes", e.target.value)} style={inputStyle} />
            </div>
            {/* SharePoint */}
            <div>
              <label style={labelStyle}>SharePoint folder URL</label>
              <input type="text" value={form.sharepoint_url} onChange={e => set("sharepoint_url", e.target.value)} placeholder="https://…" style={inputStyle} />
            </div>
            {/* Special requirements */}
            <div>
              <label style={labelStyle}>Special requirements</label>
              <textarea value={form.special_requirements} onChange={e => set("special_requirements", e.target.value)}
                placeholder="Tariff exposure, commissioning requirements, phasing, sequencing, special conditions…"
                style={{ ...inputStyle, height: 80, padding: "7px 9px", resize: "vertical" }} />
            </div>

            {saveError && (
              <div style={{ fontSize: 13, color: "var(--red)", padding: "8px 12px", background: "var(--red-lt)", borderRadius: "var(--radius)" }}>{saveError}</div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              {bidPkg && <button onClick={() => setEditing(false)} style={{ padding: "8px 16px", fontSize: 13, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>}
              <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
                {saving ? "Saving…" : "Save bid package →"}
              </button>
            </div>
          </div>
        ) : (
          // Read view
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
            {[
              ["Delivery", bidPkg?.DeliveryMethod],
              ["ISO Class", bidPkg?.CleanroomISOClass],
              ["Bid due", bidPkg?.BidDueDate ? fmtDate(bidPkg.BidDueDate) : null],
              ["Issue date", bidPkg?.IssueDate ? fmtDate(bidPkg.IssueDate) : null],
              ["Project SF", bidPkg?.ProjectSF ? Number(bidPkg.ProjectSF).toLocaleString() + " SF" : null],
              ["Location", bidPkg?.ProjectLocation],
              ["GC", bidPkg?.GeneralContractor],
              ["Architect", bidPkg?.Architect],
              ["Engineer", bidPkg?.Engineer],
              ["Bond", bidPkg?.BondRequired ? `Yes${bidPkg?.BondPercent ? ` — ${bidPkg.BondPercent}%` : ""}` : "Not required"],
              ["Wage req.", bidPkg?.WageRequirements],
              ["Addenda", bidPkg?.AddendaCount ? `${bidPkg.AddendaCount} issued` : "None"],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, color: val ? "var(--text)" : "var(--text3)" }}>{val || "—"}</div>
              </div>
            ))}
            {bidPkg?.SpecialRequirements && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Special requirements</div>
                <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>{bidPkg.SpecialRequirements}</div>
              </div>
            )}
            {bidPkg?.SharePointUrl && (
              <div style={{ gridColumn: "1 / -1" }}>
                <a href={bidPkg.SharePointUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, color: "var(--cs-blue)", textDecoration: "none", fontWeight: 500 }}>
                  📁 Open SharePoint folder →
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── AI Bid Document Summary ── */}
      <BidPackageAISummary projectId={projectId} folderUrl={form.sharepoint_url} />

      {/* ── Documents ── */}
      {bidPkg && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "16px 18px", marginBottom: 16 }}>
          <SectionHead
            icon="📄"
            title={`Documents${docs.length ? ` (${docs.length})` : ""}`}
            action={
              <button onClick={() => setShowAddDoc(true)} style={{ fontSize: 12, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>
                + Add document
              </button>
            }
          />
          {loadingDocs ? (
            <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading…</div>
          ) : docs.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text3)", padding: "12px 0" }}>No documents linked yet. Add SharePoint links for plans, specs, and addenda.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {docs.map(d => (
                <div key={d.DocumentID} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: d.IsSuperseded ? "#fafafa" : "#fff", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", opacity: d.IsSuperseded ? 0.6 : 1 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: "#f3f4f6", color: "var(--text3)" }}>{d.DocumentType}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.Title}</div>
                    {d.Version && <div style={{ fontSize: 11, color: "var(--text3)" }}>{d.Version}</div>}
                  </div>
                  {d.SharePointUrl && (
                    <a href={d.SharePointUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: "var(--cs-blue)", textDecoration: "none", whiteSpace: "nowrap" }}>
                      Open →
                    </a>
                  )}
                  {d.IsSuperseded && <span style={{ fontSize: 10, color: "var(--text3)" }}>Superseded</span>}
                </div>
              ))}
            </div>
          )}

          {showAddDoc && (
            <AddDocumentForm
              bidPackageId={bidPkg.BidPackageID}
              savedBy={user?.name}
              onSaved={doc => { setDocs(prev => [doc, ...prev]); setShowAddDoc(false); }}
              onCancel={() => setShowAddDoc(false)}
            />
          )}
        </div>
      )}

      {/* ── Spec Sections (plan-and-spec only) ── */}
      {bidPkg && (bidPkg.DeliveryMethod === "Plan-and-Spec" || bidPkg.DeliveryMethod === "Both" || form.delivery_method === "Plan-and-Spec" || form.delivery_method === "Both") && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "16px 18px" }}>
          <SectionHead
            icon="📑"
            title={`Spec Sections${specSections.length ? ` (${specSections.length})` : ""}`}
            action={
              <button onClick={() => setShowAddSpec(true)} style={{ fontSize: 12, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>
                + Add section
              </button>
            }
          />
          {specSections.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text3)", padding: "12px 0" }}>No spec sections added yet. Add CSI sections to drive the plan-and-spec checklist.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--surface2)", borderBottom: "1px solid var(--cs-border)" }}>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "var(--text3)", fontSize: 11 }}>CSI Code</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "var(--text3)", fontSize: 11 }}>Title</th>
                  <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "var(--text3)", fontSize: 11 }}>In scope</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "var(--text3)", fontSize: 11 }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {specSections.map((s, i) => (
                  <tr key={s.SpecSectionID} style={{ borderBottom: i < specSections.length - 1 ? "1px solid var(--cs-border)" : "none" }}>
                    <td style={{ padding: "7px 10px", color: "var(--text3)", fontFamily: "monospace", whiteSpace: "nowrap" }}>{s.CSICode || "—"}</td>
                    <td style={{ padding: "7px 10px", color: "var(--text)", fontWeight: 500 }}>{s.Title}</td>
                    <td style={{ padding: "7px 10px", textAlign: "center" }}>
                      <span style={{ fontSize: 14 }}>{s.InScope ? "✓" : "—"}</span>
                    </td>
                    <td style={{ padding: "7px 10px", color: "var(--text3)", fontSize: 11 }}>{s.Notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showAddSpec && (
            <AddSpecSectionForm
              bidPackageId={bidPkg.BidPackageID}
              savedBy={user?.name}
              onSaved={s => { setSpecSections(prev => [...prev, s]); setShowAddSpec(false); }}
              onCancel={() => setShowAddSpec(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Add Document inline form ───────────────────────────────────────────────────
function AddDocumentForm({ bidPackageId, savedBy, onSaved, onCancel }) {
  const [form, setForm] = useState({ type: "Plans", title: "", url: "", version: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const rows = await sqlRead(`EXEC sp_SaveBidPackageDocument
        @bid_package_id=${bidPackageId},
        @document_type='${form.type}',
        @title='${clean(form.title)}',
        @sharepoint_url='${clean(form.url)}',
        @version='${clean(form.version)}',
        @notes='${clean(form.notes)}',
        @saved_by='${clean(savedBy || "")}'`);
      onSaved({ DocumentID: rows[0]?.DocumentID, DocumentType: form.type, Title: form.title, SharePointUrl: form.url, Version: form.version, Notes: form.notes, IsSuperseded: false });
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  const inputStyle = { height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };

  return (
    <div style={{ marginTop: 12, padding: 14, background: "var(--surface2)", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)} style={{ ...inputStyle, height: 30 }}>
            {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Title</label>
          <input type="text" value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Mechanical Drawings — Rev B" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Version / Addendum #</label>
          <input type="text" value={form.version} onChange={e => set("version", e.target.value)} placeholder="e.g. Addendum 2" style={inputStyle} />
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>SharePoint URL</label>
        <input type="text" value={form.url} onChange={e => set("url", e.target.value)} placeholder="https://…" style={inputStyle} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={{ padding: "6px 12px", fontSize: 12, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
        <button onClick={save} disabled={saving || !form.title.trim()} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
          {saving ? "Saving…" : "Add document"}
        </button>
      </div>
    </div>
  );
}

// ── Add Spec Section inline form ───────────────────────────────────────────────
function AddSpecSectionForm({ bidPackageId, savedBy, onSaved, onCancel }) {
  const [form, setForm] = useState({ csi_code: "", title: "", in_scope: true, notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const rows = await sqlRead(`EXEC sp_SaveSpecSection
        @bid_package_id=${bidPackageId},
        @csi_code='${clean(form.csi_code)}',
        @title='${clean(form.title)}',
        @in_scope=${form.in_scope ? 1 : 0},
        @notes='${clean(form.notes)}',
        @saved_by='${clean(savedBy || "")}'`);
      onSaved({ SpecSectionID: rows[0]?.SpecSectionID, CSICode: form.csi_code, Title: form.title, InScope: form.in_scope, Notes: form.notes });
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  const inputStyle = { height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };

  return (
    <div style={{ marginTop: 12, padding: 14, background: "var(--surface2)", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr auto", gap: 10, alignItems: "end", marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>CSI Code</label>
          <input type="text" value={form.csi_code} onChange={e => set("csi_code", e.target.value)} placeholder="23 09 00" style={{ ...inputStyle, fontFamily: "monospace" }} />
        </div>
        <div>
          <label style={labelStyle}>Section title</label>
          <input type="text" value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. HVAC Controls" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>In scope</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, height: 30 }}>
            <input type="checkbox" checked={form.in_scope} onChange={e => set("in_scope", e.target.checked)} />
            <span style={{ fontSize: 12 }}>Yes</span>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={{ padding: "6px 12px", fontSize: 12, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
        <button onClick={save} disabled={saving || !form.title.trim()} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
          {saving ? "Saving…" : "Add section"}
        </button>
      </div>
    </div>
  );
}
